// Starting a match from the settings. In Godsmode a new match asks for the
// casters first. A match from the same seed keeps the casters of the last one.
// The first match of a page waits for the pick, see `matchWait.ts`.

import { CASTERS, CASTERS_PER_SIDE, type CasterId } from '../content/casters';
import { gameStore, newMatch } from './game';
import { isMatchWaiting, matchWaitStore } from './matchWait';
import { queueOpening } from './opening';
import { randomSeed, setCasters, setSeed, settingsStore } from './settings';
import { casterSelectStore } from './setup';

/** Closes the select and keeps the match on the board. While no match is in play, it stays open. */
export const closeCasterSelect = (): void => {
  if (!isMatchWaiting()) casterSelectStore.set(false);
};

/** Starts a match from the seed, the mode, and the casters in the settings. */
export const startMatch = (): void => {
  const { seed, mode, casters } = settingsStore.get();
  newMatch(seed, mode, mode === 'gods' ? casters : null);
};

/**
 * The first match of a page with no match to load. In Godsmode it waits for the
 * casters: the store gets a match nobody plays, and the select opens.
 */
export const startFirstMatch = (): void => {
  startMatch();
  if (settingsStore.get().mode !== 'gods') return;
  matchWaitStore.set(true);
  casterSelectStore.set(true);
};

/** Starts a match from a new seed. In Godsmode the player picks the casters first. */
export const startNewMatch = (): void => {
  setSeed(randomSeed());
  if (settingsStore.get().mode === 'gods') casterSelectStore.set(true);
  else startMatch();
};

/** Casters drawn with no seed, as many as a side has. */
const randomCasters = (): CasterId[] => {
  const pool = CASTERS.map((caster) => caster.id);
  const drawn: CasterId[] = [];
  while (drawn.length < CASTERS_PER_SIDE && pool.length > 0) {
    drawn.push(...pool.splice(Math.floor(Math.random() * pool.length), 1));
  }
  return drawn;
};

/** The player picked the casters, or asked for random ones with null. */
export const pickCasters = (casters: readonly CasterId[] | null): void => {
  setCasters(casters ?? randomCasters());
  // The match waits while it starts, so no driver rolls before the beats of the opening are in the queue.
  matchWaitStore.set(true);
  startMatch();
  queueOpening(gameStore.get());
  matchWaitStore.set(false);
  closeCasterSelect();
};
